import { useEffect, useState, useMemo } from 'react';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { useIntl, FormattedMessage } from 'react-intl';
import { useToast } from '../../contexts/ToastContext';
import { useUser } from '../../contexts/UserContext';
import { useConfirmationDialog } from '../../utils/ConfirmationDialogContext';
import { formatDateToApi, validateDates } from '../../utils/UtilFunctions';
import { api } from '../../services/api-client';
import { PaymentFrequency } from '../../types/coach/coach-plan';
import { CoachPlanDialog } from '../coach/CoachPlanDialog';
import { useCoachPlanDialog } from '../../hooks/coach/useCoachPlanDialog';

interface ICoachPlanOption {
  id: number;
  name: string;
  paymentFrequency: PaymentFrequency;
}

interface IAssignSubscriptionDialogProps {
  studentId: number;
  onClose: () => void;
}

const AssignSubscriptionDialog = ({ studentId, onClose }: IAssignSubscriptionDialogProps) => {
  const intl = useIntl();
  const { user } = useUser();
  const { showToast } = useToast();
  const { showConfirmationDialog } = useConfirmationDialog();

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [loading, setLoading] = useState(false);
  const [coachPlans, setCoachPlans] = useState<ICoachPlanOption[]>([]);
  const [selectedCoachPlan, setSelectedCoachPlan] = useState<number | null>(null);
  const [plansLoaded, setPlansLoaded] = useState(false);

  const loadCoachPlans = async (): Promise<ICoachPlanOption[]> => {
    const { data } = await api.subscription.fetchCoachPlans();
    const plans = data ?? [];
    setCoachPlans(plans);
    return plans;
  };

  const planDialog = useCoachPlanDialog(async () => {
    const prevIds = new Set(coachPlans.map((p) => p.id));
    const plans = await loadCoachPlans();
    const created = plans.find((p) => !prevIds.has(p.id));
    if (created) setSelectedCoachPlan(created.id);
  });

  const selectedPlan = useMemo(
    () => coachPlans.find((p) => p.id === selectedCoachPlan) ?? null,
    [coachPlans, selectedCoachPlan]
  );

  useEffect(() => {
    if (!user?.userId) return;
    (async () => {
      try {
        await loadCoachPlans();
      } catch (error) {
        showToast('error', intl.formatMessage({ id: 'common.error' }), (error as Error).message);
      } finally {
        setPlansLoaded(true);
      }
    })();
  }, [user?.userId, showToast, intl]);

  useEffect(() => {
    if (!startDate || !selectedPlan) return;
    const freq = selectedPlan.paymentFrequency;
    if (freq === 'per_session') {
      setEndDate(null);
      return;
    }

    const calculated = new Date(startDate);
    if (freq === 'monthly') {
      calculated.setMonth(calculated.getMonth() + 1);
    } else if (freq === 'weekly') {
      calculated.setDate(calculated.getDate() + 7);
    }
    setEndDate(calculated);
  }, [startDate, selectedPlan]);

  const handleAssign = () => {
    if (!selectedCoachPlan) {
      showToast(
        'error',
        intl.formatMessage({ id: 'common.error' }),
        intl.formatMessage({ id: 'assignSubscription.error.selectPlan' })
      );
      return;
    }

    const isPerSession = selectedPlan?.paymentFrequency === 'per_session';

    if (!isPerSession) {
      const { isValid, message } = validateDates(startDate, endDate, intl);
      if (!isValid) {
        showToast('error', intl.formatMessage({ id: 'common.error' }), message);
        return;
      }
    } else if (!startDate) {
      showToast(
        'error',
        intl.formatMessage({ id: 'common.error' }),
        intl.formatMessage({ id: 'assignSubscription.error.startDateRequired' })
      );
      return;
    }

    const body = {
      clientId: studentId,
      startDate: startDate ? formatDateToApi(startDate) : null,
      endDate: isPerSession ? null : endDate ? formatDateToApi(endDate) : null,
      coachPlanId: selectedCoachPlan
    };

    showConfirmationDialog({
      message: intl.formatMessage({ id: 'assignSubscription.confirmation.message' }),
      header: intl.formatMessage({ id: 'common.confirmation' }),
      icon: 'pi pi-exclamation-triangle',
      accept: () => confirmAssign(body)
    });
  };

  const confirmAssign = async (body: Record<string, unknown>) => {
    try {
      setLoading(true);
      await api.subscription.assignSubscription(body);
      showToast(
        'success',
        intl.formatMessage({ id: 'common.success' }),
        intl.formatMessage({ id: 'assignSubscription.success' })
      );
      onClose();
    } catch (error) {
      showToast('error', intl.formatMessage({ id: 'common.error' }), (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const isPerSession = selectedPlan?.paymentFrequency === 'per_session';

  return (
    <div className="p-3">
      {plansLoaded && coachPlans.length === 0 && (
        <div
          className="p-3 mb-3"
          style={{
            background: 'rgba(163, 230, 53, 0.18)',
            borderRadius: 'var(--ios-radius-md)',
            border: '1px solid rgba(10, 10, 10, 0.2)'
          }}
        >
          <p className="m-0 mb-2">
            <FormattedMessage id="assignSubscription.noPlansYet" />
          </p>
          <Button
            label={intl.formatMessage({ id: 'assignSubscription.createFirstPlan' })}
            icon="pi pi-plus"
            onClick={planDialog.openCreate}
            className="p-button-sm"
          />
        </div>
      )}

      {coachPlans.length > 0 && (
        <div className="p-field">
          <label htmlFor="coachPlan">
            <FormattedMessage id="plan" />
          </label>
          <div className="flex gap-2 align-items-center">
            <Dropdown
              id="coachPlan"
              options={coachPlans}
              optionLabel="name"
              optionValue="id"
              value={selectedCoachPlan}
              onChange={(e) => setSelectedCoachPlan(e.value)}
              className="flex-grow-1"
            />
            <Button
              icon="pi pi-plus"
              tooltip={intl.formatMessage({ id: 'assignSubscription.createInlinePlan' })}
              tooltipOptions={{ position: 'top' }}
              onClick={planDialog.openCreate}
              className="p-button-text"
            />
          </div>
        </div>
      )}

      <div className="p-field w-full">
        <label htmlFor="startDate">
          <FormattedMessage id="startDate" />
        </label>
        <Calendar
          id="startDate"
          locale={intl.locale}
          dateFormat="dd/mm/yy"
          value={startDate}
          onChange={(e) => setStartDate(e.value as Date | null)}
          showIcon
          className="w-full"
        />
      </div>

      {selectedPlan && !isPerSession && (
        <div
          style={{
            padding: '0.6rem 0.75rem',
            borderRadius: 'var(--ios-radius-md)',
            background: 'rgba(245, 158, 11, 0.1)',
            color: '#b45309',
            fontSize: '0.85rem',
            lineHeight: 1.4
          }}
        >
          <i className="pi pi-info-circle" style={{ marginRight: '0.4rem' }} />
          <FormattedMessage
            id="assignSubscription.debtNotice"
            defaultMessage="El cliente arranca con deuda. Los días restantes se activan recién cuando registres el primer pago."
          />
        </div>
      )}

      <div className="flex justify-content-end">
        <Button
          label={intl.formatMessage({ id: 'common.save' })}
          icon="pi pi-check"
          loading={loading}
          onClick={handleAssign}
        />
      </div>

      <CoachPlanDialog
        visible={planDialog.visible}
        loading={planDialog.loading}
        mode={planDialog.mode}
        form={planDialog.form}
        onHide={planDialog.close}
        onChange={planDialog.setField}
        onSave={planDialog.save}
      />
    </div>
  );
};

export default AssignSubscriptionDialog;
